import styled, { css } from 'styled-components'

import { Button } from 'components/Button'
import { color, font, fontWeight, SubheadlineStyle } from 'styles/global'
import { space } from 'styles/utils'

const smallPlayerStyles = css`
  background: ${color('smallPlayer')};
  bottom: 72px;
  display: flex;
  flex-direction: row;
  height: 60px;
  left: 0;
  margin: 0 auto;
  max-width: 900px;
  padding: ${space(2)} ${space(4)} 0;
  position: fixed;
  right: 0;
  z-index: 10;

  > button {
    position: absolute;
    right: 0;
    top: ${space(3)};
    z-index: 2;
  }

  @media only screen and (min-width: 650px) {
    height: 130px;
    background: ${color('primaryBackground')};
    > button {
      top: 0;
    }
  }

  @media only screen and (min-width: 960px) {
    bottom: 0;
  }
`

export const PlayerImage = styled.div`
  @media only screen and (max-height: 550px) {
    > div {
      height: 40vh;
      margin: 0 auto;
      background: ${color('secondaryBackground')};
    }
    img {
      height: 100%;
      width: auto;
    }
  }
`

export const Wrapper = styled.section<{ small?: boolean }>`
  margin-bottom: 0;
  padding: ${space(4)} ${space(8)};

  @media only screen and (max-height: 700px) and (max-width: 650px) {
    ${({ small }) =>
      !small &&
      css`
        background: ${color('primaryBackground')};
        bottom: var(--navigation-height);
        left: 0;
        position: absolute;
        right: 0;
      `}
  }

  @media only screen and (max-height: 700px) {
    padding: ${space(2)} ${space(8)} 0;
  }

  ${({ small }) => (small ? smallPlayerStyles : '')};
`

export const Controls = styled.div<{ small?: boolean }>`
  display: flex;
  justify-content: space-between;
  margin: ${space(6)} auto ${space(4)};
  max-width: 300px;
  padding: 0 ${space(3)};
  width: 100%;

  @media only screen and (max-height: 800px) {
    margin: ${space(3)} auto ${space(2)};
  }

  @media only screen and (max-height: 550px) {
    margin: 0 auto;
  }

  ${({ small }) =>
    small &&
    css`
      margin: ${space(1)} auto;
      padding: 0;
    `};
`

export const SkipButton = styled(Button)`
  span:first-child {
    font-family: ${font('base')};
    font-size: 0.823rem;
    font-weight: ${fontWeight('base')};
    padding-left: 2px;
    padding-top: 2px;
    position: absolute;
  }
  @media (hover: none) {
    &:hover {
      color: ${color('interactive')};
    }
  }
`

export const PlayButton = styled.button`
  align-items: center;
  background: none;
  border: 2px solid ${color('interactive')};
  border-radius: 54px;
  color: ${color('interactive')};
  display: flex;
  height: 54px;
  justify-content: center;
  width: 54px;

  &:not([disabled]):hover {
    border: 2px solid ${color('interactiveHover')};
    color: ${color('interactiveHover')};
  }

  .loading {
    transform: scale(0.8);
  }
  &[disabled] {
    border: 2px solid ${color('interactiveSecondary')};
    pointer-events: none;
  }

  @media only screen and (max-height: 550px) {
    height: 44px;
    width: 44px;
    margin: auto 0;
  }
`

export const Error = styled.div`
  color: ${color('secondaryText')};
  font-size: 14px;
  margin-bottom: ${space(7)};
  > * + * {
    margin-top: ${space(2)};
  }
  .icon {
    color: ${color('primaryText')};
  }
`

export const Duration = styled.strong`
  color: ${color('secondaryHeadingText')};
  font-family: ${font('base')};
  font-size: 0.823rem;
  font-weight: ${fontWeight('bold')};
  line-height: 1.5;

  span {
    display: none;
  }

  @media only screen and (min-width: 650px) {
    span {
      display: unset;
    }
    > div {
      display: none;
    }
  }
`

export const Time = styled.div<{ loaded?: boolean }>`
  color: ${color('secondaryHeadingText')};
  flex: 1;
  font-family: ${font('base')};
  font-size: 0.823rem;
  font-weight: ${fontWeight('bold')};
  line-height: 1.5;
  pointer-events: none;

  &:not(:first-child):last-child {
    text-align: right;
  }

  &:last-child {
    color: ${color('primaryText')};
  }
  ${({ loaded }) =>
    loaded &&
    css`
      color: ${color('secondaryHeadingText')};
      &:last-child {
        color: ${color('secondaryHeadingText')};
      }
    `}
`

export const PlayerContainer = styled.div<{ small?: boolean }>`
  flex-direction: column;
  width: 100%;

  ${({ small }) =>
    small &&
    css`
      display: none;
      flex: 1;
      margin: 0 auto;
      min-width: 220px;
      padding: 0 ${space(4)};
      @media only screen and (min-width: 650px) {
        display: flex;
      }
    `};
`

export const MetaBlock = styled.div<{ small?: boolean }>`
  display: flex;
  flex-direction: column;
  pointer-events: none;
  text-align: center;
  width: 100%;

  ${({ small }) =>
    small &&
    css`
      flex: 1;
      text-align: left;
      @media only screen and (min-width: 650px) {
        justify-content: center;
      }
    `}
`

export const PlayerTime = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  pointer-events: none;
  width: 100%;
`

export const PlayerTitle = styled.h2<{ allowEllipsis?: boolean }>`
  ${SubheadlineStyle};
  margin: 0;
  margin-bottom: 0;
  pointer-events: none;
  width: 100%;

  @media only screen and (min-width: 650px) {
    margin-bottom: ${space(1)};
  }
  @media only screen and (max-width: 650px) {
    ${({ allowEllipsis }) =>
      allowEllipsis &&
      `
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      text-align: left;
      width: 80%;
  `}
  }
`

export const ImageContainer = styled.div`
  margin: 0 auto ${space(3)} auto;
  img {
    max-height: calc(var(--vh) * 0.5);
    min-height: 200px;
  }
`
